/** Metrics sagas: GET_METRICS, GET_METRICS_BY_FILTER. */

import { put, call, all, takeEvery } from 'redux-saga/effects'
import { store as reduxStore } from 'index'
import {
  getByType as getMetricsByType,
  get as getMetrics,
  DcuPoint,
  MauPoint,
  McuPoint,
  MetricsApp,
  PccPoint,
  RecordingPoint,
  VideoPoint,
  VoicePoint,
} from 'services/metrics'
import _ from 'lodash'
import {
  checkIfAnalyticsIsSupported,
  getAppMetricHost,
  getMetricDayTimestamp,
  getMetricMonthTimestamp,
  getMetricYearTimestamp,
  getMetricHoursTimestamp,
  getMetricMinutesTimestamp,
  getTimestampOfMonth,
  getMetricMonthFromTimestamp,
  getMetricMonthFromMonthNo,
} from 'helpers'
import actions from './actions'

/**
 * Metrics slice sagas. `GET_METRICS_BY_FILTER` is knowingly broken in production and
 * ported as-is — see docs/overview-migration/PROGRESS.md before touching it.
 */

// Cast at the registration boundary: `{ payload }` workers don't satisfy
// redux-saga's `A extends Action`. Runtime unchanged.
type SagaWorker = (action: any) => any

// Local 'YYYY-MM-DD' for an x-axis timestamp. Local, not UTC: the axis is built
// from local midnights, so toISOString would shift the day.
const toDayKey = (timestamp: number): string => {
  const d = new Date(timestamp)
  const month = `${d.getMonth() + 1}`.padStart(2, '0')
  const day = `${d.getDate()}`.padStart(2, '0')
  return `${d.getFullYear()}-${month}-${day}`
}

// Steps the calendar rather than adding 24h, so every slot stays on a real local
// midnight and `toDayKey` still matches across a DST change.
const addDays = (start: number, offset: number): number => {
  const d = new Date(start)
  d.setDate(d.getDate() + offset)
  return d.getTime()
}

/** Window sizes in points, and the app to read. Every field has a fallback. */
export interface GetMetricsPayload {
  data?: MetricsApp
  appSettings?: Record<string, unknown>
  dcuCurrentFilter?: number
  pcuCurrentFilter?: number
  ccuCurrentFilter?: number
  voiceCurrentFilter?: number
  videoCurrentFilter?: number
  mauCurrentFilter?: number
  mcuCurrentFilter?: number
}

export interface GetMetricsByFilterPayload {
  data?: MetricsApp
  appSettings?: Record<string, unknown>
  /** Which metric to refetch — 'ccu', 'pcu', 'dau', 'mau', 'voice' or 'video'. */
  type: string
  /** Window size in points, in the unit that `type` implies. */
  filter: number
}

export function* GET_METRICS({ payload }: { payload: GetMetricsPayload }) {
  yield put({
    type: actions.SET_STATE,
    payload: {
      metricsLoading: true,
    },
  })

  try {
    if (!payload.dcuCurrentFilter) payload.dcuCurrentFilter = 30
    if (!payload.pcuCurrentFilter) payload.pcuCurrentFilter = 30
    if (!payload.ccuCurrentFilter) payload.ccuCurrentFilter = 60

    if (!payload.voiceCurrentFilter) payload.voiceCurrentFilter = 30
    if (!payload.videoCurrentFilter) payload.videoCurrentFilter = 30

    if (!payload.mauCurrentFilter) payload.mauCurrentFilter = 3
    if (!payload.mcuCurrentFilter) payload.mcuCurrentFilter = 3

    if (!payload.data) payload.data = reduxStore.getState().apps.active
    if (!payload.appSettings) payload.appSettings = reduxStore.getState().apps.appSettings

    let metricsPlanSupport = false
    metricsPlanSupport = checkIfAnalyticsIsSupported(payload.data)
    let requestTimeOut = false
    const nowDate = new Date()

    const endRange = nowDate.getTime()
    // `string | number` to match MetricsState: the API sends MAU/MCU/DCU as
    // numbers and PCC/minutes as strings.
    let metricsAUCDay: string | number = '0'
    let metricsAUCMonth: string | number = '0'
    let metricsAUCPeak: string | number = '0'
    let metricsAVoMToday: string | number = '0'
    let metricsAViMToday: string | number = '0'
    let metricsAReMToday: string | number = '0'

    // DCU

    const startRangeTempDCU = endRange - (payload.dcuCurrentFilter - 1) * 24 * 60 * 60 * 1000
    const startRangeTempDateDCU = new Date(startRangeTempDCU)
    const startRangeDateDCU = new Date(
      startRangeTempDateDCU.getFullYear(),
      startRangeTempDateDCU.getMonth(),
      startRangeTempDateDCU.getDate(),
    )
    const startRangeDCU = startRangeDateDCU.getTime()

    const metricsDcuXaxisValues: any[] = [startRangeDCU]
    let metricsDcu: any[] = [metricsDcuXaxisValues[0], 0]
    const metricsDcuNoData: any[] = [metricsDcuXaxisValues[0], 0]
    let isDCUEmpty = true
    for (let i = 1; i <= payload.dcuCurrentFilter; i += 1) {
      if (i === payload.dcuCurrentFilter) {
        metricsDcuXaxisValues[i] = endRange
        metricsDcu[i] = [endRange, 0]
        metricsDcuNoData[i] = [endRange, 0]
      } else {
        metricsDcuXaxisValues[i] = startRangeDCU + i * 24 * 60 * 60 * 1000
      }
    }

    for (let i = 0; i < payload.dcuCurrentFilter; i += 1) {
      metricsDcu[i] = [metricsDcuXaxisValues[i], 0]
      metricsDcuNoData[i] = [metricsDcuXaxisValues[i], 0]
    }
    let dcuServiceParams = {}
    dcuServiceParams = {
      type: 'dcu',
      startRange: metricsDcuXaxisValues[0],
      endRange,
    }
    let successDcu: any = { data: false, appSettings: [] }

    // MAU
    const metricsMauXaxisValues: any[] = [
      getTimestampOfMonth(2, 1) * 1000,
      getTimestampOfMonth(1) * 1000,
      endRange,
    ]

    let metricsMau: any[] = []
    const metricsMauNoData: any[] = []
    let isMAUEmpty = true

    let mauServiceParams = {}
    mauServiceParams = {
      type: 'mau',
      startRange: metricsMauXaxisValues[0],
      endRange,
    }
    let successMau: any = { data: false, appSettings: [] }

    // MCU
    const metricsMcuXaxisValues: any[] = [
      getTimestampOfMonth(2, 1) * 1000,
      getTimestampOfMonth(1) * 1000,
      endRange,
    ]

    let metricsMcu: any[] = []
    const metricsMcuNoData: any[] = []
    let isMCUEmpty = true

    let mcuServiceParams = {}
    mcuServiceParams = {
      type: 'mcu',
      startRange: metricsMcuXaxisValues[0],
      endRange,
    }
    let successMcu: any = { data: false, appSettings: [] }

    // PEAK CCU

    const startRangePeakTemp = endRange - (payload.pcuCurrentFilter - 1) * 24 * 60 * 60 * 1000
    const startRangePeakTempDate = new Date(startRangePeakTemp)
    const startRangePeakDate = new Date(
      startRangePeakTempDate.getFullYear(),
      startRangePeakTempDate.getMonth(),
      startRangePeakTempDate.getDate(),
    )
    const startRangePeak = startRangePeakDate.getTime()

    const metricsPeakXaxisValues: any[] = [startRangePeak]
    let metricsPeak: any[] = [metricsPeakXaxisValues[0], 0]
    const metricsPeakNoData: any[] = [metricsPeakXaxisValues[0], 0]
    let isPeakEmpty = true
    for (let i = 1; i <= payload.pcuCurrentFilter; i += 1) {
      if (i === payload.pcuCurrentFilter) {
        metricsPeakXaxisValues[i] = endRange
        metricsPeak[i] = [endRange, 0]
        metricsPeakNoData[i] = [endRange, 0]
      } else {
        metricsPeakXaxisValues[i] = startRangePeak + i * 24 * 60 * 60 * 1000
      }
    }

    for (let i = 0; i < payload.pcuCurrentFilter; i += 1) {
      metricsPeak[i] = [metricsPeakXaxisValues[i], 0]
      metricsPeakNoData[i] = [metricsPeakXaxisValues[i], 0]
    }
    let successPeak: any = { data: false }

    const peakCallServiceParams = { startRange: metricsPeakXaxisValues[0], endRange, type: 'pcc' }

    // Voice and Video Analytics

    const startRangeVideoTemp = endRange - (payload.videoCurrentFilter - 1) * 24 * 60 * 60 * 1000

    const startRangeVideoTempDate = new Date(startRangeVideoTemp)

    const startRangeVideoDate = new Date(
      startRangeVideoTempDate.getFullYear(),
      startRangeVideoTempDate.getMonth(),
      startRangeVideoTempDate.getDate(),
    )

    const startRangeVoice = startRangeVideoDate.getTime()
    const startRangeVideo = startRangeVideoDate.getTime()

    const metricsVoiceXaxisValues: any[] = [startRangeVideo]
    const metricsVideoXaxisValues: any[] = [startRangeVideo]
    const metricsRecordingXaxisValues: any[] = [startRangeVideo]

    let metricsVoice: any[] = [metricsVoiceXaxisValues[0], 0]
    let metricsVideo: any[] = [metricsVideoXaxisValues[0], 0]
    let metricsRecording: any[] = [metricsRecordingXaxisValues[0], 0]

    const metricsVoiceNoData: any[] = [metricsVoiceXaxisValues[0], 0]
    const metricsVideoNoData: any[] = [metricsVideoXaxisValues[0], 0]
    const metricsRecordingNoData: any[] = [metricsRecordingXaxisValues[0], 0]

    for (let i = 0; i < payload.videoCurrentFilter; i += 1) {
      metricsVoiceXaxisValues[i] = addDays(startRangeVoice, i)
      metricsVoice[i] = [metricsVoiceXaxisValues[i], 0]
      metricsVoiceNoData[i] = [metricsVoiceXaxisValues[i], 0]
    }
    for (let i = 0; i < payload.videoCurrentFilter; i += 1) {
      metricsVideoXaxisValues[i] = addDays(startRangeVideo, i)
      metricsVideo[i] = [metricsVideoXaxisValues[i], 0]
      metricsVideoNoData[i] = [metricsVideoXaxisValues[i], 0]
    }
    for (let i = 0; i < payload.videoCurrentFilter; i += 1) {
      metricsRecordingXaxisValues[i] = addDays(startRangeVideo, i)
      metricsRecording[i] = [metricsRecordingXaxisValues[i], 0]
      metricsRecordingNoData[i] = [metricsRecordingXaxisValues[i], 0]
    }

    let successVoice: any = { data: false }
    let successVideo: any = { data: false }
    let successRecording: any = { data: false }

    const voiceVideoCallServiceParams = {
      startRange: metricsVoiceXaxisValues[0],
      endRange,
    }

    const getMetricCalls = yield call(
      getMetrics as any,
      payload.data,
      mauServiceParams,
      peakCallServiceParams,
      voiceVideoCallServiceParams,
      mcuServiceParams,
      dcuServiceParams,
    )

    if (getMetricCalls.metricSuccess !== undefined && getMetricCalls.metricSuccess) {
      successMau = getMetricCalls.mau
      successPeak = getMetricCalls.peakCcu
      successVoice = getMetricCalls.voice
      successVideo = getMetricCalls.video
      successRecording = getMetricCalls.recording
      successMcu = getMetricCalls.mcu
      successDcu = getMetricCalls.dcu
    } else if (getMetricCalls.err_code !== undefined) {
      requestTimeOut = true
    }

    if (!requestTimeOut) {
      if (successMau.data) {
        const metricsMauDataResponse: MauPoint[] = successMau.data

        if (metricsMauDataResponse.length > 0) {
          _.forEach(metricsMauDataResponse, function fn(mdx, key) {
            isMAUEmpty = false
            metricsMau[key] = [mdx, metricsMauDataResponse[key].MAU]
            if (mdx.date) {
              const monthTemp = _.split(mdx.date, '-')

              metricsMau[key] = {
                x: getMetricMonthFromMonthNo(_.toNumber(monthTemp[1])),
                y: metricsMauDataResponse[key].MAU,
              }

              metricsAUCMonth = metricsMauDataResponse[key].MAU
            }
          })
        }
      }
      metricsMau = metricsMau.slice(-3)

      if (successMcu.data) {
        const metricsMcuDataResponse: McuPoint[] = successMcu.data

        if (metricsMcuDataResponse.length > 0) {
          _.forEach(metricsMcuDataResponse, function fn(mdx, key) {
            isMCUEmpty = false
            // ⚠️ `date` is 'YYYY-MM', so this is always false and the block never
            // runs — MCU charts stay zero-filled. Preserved; see PROGRESS.md.
            if (Number(mdx.date) > 0) {
              metricsMcu[key] = {
                x: getMetricMonthFromMonthNo(mdx.date),
                y: metricsMcuDataResponse[key].MCU,
              }

              if (metricsMcuDataResponse.length === key + 1)
                metricsAUCMonth = metricsMcuDataResponse[key].MCU
            }
          })
        }
      }
      metricsMcu = metricsMcu.slice(-3)

      if (successPeak.data) {
        const metricsPeakDataResponse: PccPoint[] = successPeak.data
        if (metricsPeakDataResponse.length > 0) {
          _.forEach(metricsPeakDataResponse, function fn(mdx, key) {
            const fullDate: any[] = _.split(mdx.date, '-', 3)
            isPeakEmpty = false
            const date = new Date(fullDate[0], fullDate[1] - 1, fullDate[2], 0, 0, 0)
            const timestampLocal = date.getTime()

            metricsPeak[key] = [timestampLocal, metricsPeakDataResponse[key].PCC]

            metricsAUCPeak = metricsPeakDataResponse[key].PCC
          })
        }
      }
      metricsPeak = metricsPeak.slice(-30)

      // DCU
      if (successDcu.data) {
        const metricsDcuDataResponse: DcuPoint[] = successDcu.data
        if (metricsDcuDataResponse.length > 0) {
          _.forEach(metricsDcuDataResponse, function fn(mdx, key) {
            const fullDate: any[] = _.split(mdx.date, '-', 3)
            isDCUEmpty = false
            const date = new Date(fullDate[0], fullDate[1] - 1, fullDate[2], 0, 0, 0)
            const timestampLocal = date.getTime()

            metricsDcu[key] = [timestampLocal, metricsDcuDataResponse[key].DCU]

            if (metricsDcuDataResponse.length === key + 1)
              metricsAUCDay = metricsDcuDataResponse[key].DCU
          })
        }
      }

      // Voice and Video Analytics

      if (successVoice.data) {
        const metricsVoiceDataResponse: VoicePoint[] = successVoice.data
        if (metricsVoiceDataResponse.length > 0) {
          // Matched by date, not position: the API returns a different number of
          // points than the window has slots, so indexing by key plotted values
          // on the wrong day and dropped the newest one.
          const voiceByDate = _.keyBy(metricsVoiceDataResponse, 'date')
          _.forEach(metricsVoiceXaxisValues, function fn(mvxv, key) {
            const point = voiceByDate[toDayKey(mvxv)]
            metricsVoice[key] = [mvxv, point ? point.voice_minutes : 0]
          })
          metricsAVoMToday = _.last(metricsVoice)?.[1] ?? 0
        }
      } else {
        metricsVoice = []
      }

      if (successVideo.data) {
        const metricsVideoDataResponse: VideoPoint[] = successVideo.data
        if (metricsVideoDataResponse.length > 0) {
          const videoByDate = _.keyBy(metricsVideoDataResponse, 'date')
          _.forEach(metricsVideoXaxisValues, function fn(mvxv, key) {
            const point = videoByDate[toDayKey(mvxv)]
            metricsVideo[key] = [mvxv, point ? point.video_minutes : 0]
          })
          metricsAViMToday = _.last(metricsVideo)?.[1] ?? 0
        }
      } else {
        metricsVideo = []
      }

      if (successRecording.data) {
        const metricsRecordingDataResponse: RecordingPoint[] = successRecording.data
        if (metricsRecordingDataResponse.length > 0) {
          const recordingByDate = _.keyBy(metricsRecordingDataResponse, 'date')
          _.forEach(metricsRecordingXaxisValues, function fn(mvxv, key) {
            const point = recordingByDate[toDayKey(mvxv)]
            metricsRecording[key] = [mvxv, point ? point.recording_minutes : 0]
          })
          metricsAReMToday = _.last(metricsRecording)?.[1] ?? 0
        }
      } else {
        metricsRecording = []
      }
    }

    metricsDcu = _.compact(metricsDcu)
    if (isDCUEmpty) {
      metricsDcu = []
    }
    metricsMau = _.compact(metricsMau)
    if (isMAUEmpty) {
      metricsMau = []
    }

    metricsMcu = _.compact(metricsMcu)

    if (isMCUEmpty) {
      metricsMcu = []
    }

    if (isPeakEmpty) {
      metricsPeak = []
    }

    if (!metricsPlanSupport) {
      metricsMau = metricsMauNoData
      metricsMcu = metricsMcuNoData
      metricsDcu = metricsDcuNoData
      metricsPeak = metricsPeakNoData
    }

    metricsVoice = _.compact(metricsVoice)
    metricsVideo = _.compact(metricsVideo)
    metricsRecording = _.compact(metricsRecording)

    if (requestTimeOut) {
      window.triggerNotification?.('Request Time-out ', 'error')
      yield put({
        type: actions.SET_STATE,
        payload: {
          metricsDcu: [],
          metricsMcu: [],
          metricsMau: [],
          metricsPeak: [],
          metricsVoice: [],
          metricsVideo: [],
          metricsRecording: [],
          metricsAUCDay: 0,
          metricsAUCPeak: 0,
          metricsAReMToday: 0,
          metricsAVoMToday: 0,
          metricsAViMToday: 0,
          metricsAUCMonth: 0,
          metricsPlanSupport,
          metricsLoading: false,
          metricsUpdateSuccess: false,
        },
      })
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: {
          metricsDcu,
          metricsMcu,
          metricsMau,
          metricsPeak,
          metricsVoice,
          metricsVideo,
          metricsRecording,
          metricsAUCDay,
          metricsAUCPeak,
          metricsAReMToday,
          metricsAVoMToday,
          metricsAViMToday,
          metricsAUCMonth,
          metricsPlanSupport,
          metricsLoading: false,
          metricsUpdateSuccess: true,
        },
      })
    }

    return successMau
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        metricsLoading: false,
      },
    })
    window.triggerNotification?.('Failed to fetch metrics', 'error')
  }
}

export function* GET_METRICS_BY_FILTER({ payload }: { payload: GetMetricsByFilterPayload }) {
  if (!payload.data) payload.data = reduxStore.getState().apps.active
  if (!payload.appSettings) payload.appSettings = reduxStore.getState().apps.appSettings

  let metricsPlanSupport = false
  metricsPlanSupport = checkIfAnalyticsIsSupported(payload.data)

  try {
    let requestTimeOut = false
    const nowDate = new Date()

    const endRange = nowDate.getTime()
    const timezoneDiffInMs = nowDate.getTimezoneOffset() * 60 * 1000

    if (payload.type === 'ccu') {
      let metricsAUCMinute = '0'
      // get CCU
      let isCCUEmpty = true
      const endRangeCcu = endRange
      const metricsCcuXaxisValues: any[] = []
      for (let i = payload.filter; i > 0; i -= 1) {
        if (i === payload.filter) {
          metricsCcuXaxisValues[payload.filter - 1] = endRangeCcu
        } else metricsCcuXaxisValues[payload.filter - 1 - i] = endRangeCcu - i * 60 * 1000
      }

      let metricsCcu: any[] = []
      const metricsCcuNoData: any[] = []
      for (let i = 0; i < payload.filter; i += 1) {
        metricsCcu[i] = [metricsCcuXaxisValues[i], 0]
        metricsCcuNoData[i] = [metricsCcuXaxisValues[i], 0]
      }

      let successCcu: any = { data: false }
      const ccuServiceParams = {
        startRange: metricsCcuXaxisValues[0],
        endRange: endRangeCcu,
      }
      const getMetricCalls = yield call(
        getMetricsByType as any,
        payload.data,
        getAppMetricHost(payload.appSettings),
        payload.type,
        ccuServiceParams,
      )

      if (getMetricCalls.data !== undefined && getMetricCalls.data) {
        successCcu = getMetricCalls.data
      } else if (getMetricCalls.err_code !== undefined) {
        requestTimeOut = true
      }
      if (!requestTimeOut) {
        if (getMetricCalls.data) {
          const metricsCcuDataResponse: any = successCcu
          if (metricsCcuDataResponse.length > 0) {
            const lastOfCCU: any = _.last(metricsCcuDataResponse)
            metricsAUCMinute = lastOfCCU.users_count
            _.forEach(metricsCcuDataResponse, function fn(mdx, key) {
              const checkIndex = _.findIndex(metricsCcuXaxisValues, function fn2(mmau: any) {
                const day = getMetricDayTimestamp(mmau)
                const month = getMetricMonthTimestamp(mmau)
                const year = getMetricYearTimestamp(mmau)
                const hour = getMetricHoursTimestamp(mmau)
                const minute = getMetricMinutesTimestamp(mmau)
                return (
                  mdx.day === day &&
                  mdx.month === month &&
                  mdx.year === year &&
                  mdx.hour === hour &&
                  mdx.minute === minute
                )
              })
              if (checkIndex >= 0) {
                isCCUEmpty = false
                if (
                  mdx.day > 0 &&
                  mdx.year > 0 &&
                  mdx.month >= 0 &&
                  mdx.hour >= 0 &&
                  mdx.minute >= 0
                ) {
                  const date = new Date(mdx.year, mdx.month, mdx.day, mdx.hour, mdx.minute, 0)
                  const timestampLocal = date.getTime() - timezoneDiffInMs
                  metricsCcu[checkIndex] = [timestampLocal, metricsCcuDataResponse[key].users_count]
                  const dayToday = getMetricDayTimestamp(endRange)
                  const monthToday = getMetricMonthTimestamp(endRange)
                  const yearToday = getMetricYearTimestamp(endRange)
                  const hourNow = getMetricHoursTimestamp(endRange)
                  const minuteNow = getMetricMinutesTimestamp(endRange)
                  if (
                    mdx.day === dayToday &&
                    mdx.month === monthToday &&
                    mdx.year === yearToday &&
                    mdx.hour === hourNow &&
                    mdx.minute === minuteNow
                  )
                    metricsAUCMinute = metricsCcuDataResponse[key].users_count
                }
              }
            })
          }
        }
      }

      metricsCcu = _.compact(metricsCcu)
      if (isCCUEmpty) {
        metricsCcu = []
      }
      if (!metricsPlanSupport) {
        metricsCcu = metricsCcuNoData
      }
      if (requestTimeOut) {
        window.triggerNotification?.('Request Time-out ', 'error')
      } else {
        yield put({
          type: actions.SET_STATE,
          payload: {
            metricsCcu,
            metricsAUCMinute,
            metricsUpdateSuccess: true,
          },
        })
      }
    } else if (payload.type === 'pcu') {
      let metricsAUCPeak = '0'

      // PEAK CCU

      const startRangePeakTemp = endRange - (payload.filter - 1) * 24 * 60 * 60 * 1000
      const startRangePeakTempDate = new Date(startRangePeakTemp)
      const startRangePeakDate = new Date(
        startRangePeakTempDate.getFullYear(),
        startRangePeakTempDate.getMonth(),
        startRangePeakTempDate.getDate(),
      )
      const startRangePeak = startRangePeakDate.getTime()

      const metricsPeakXaxisValues: any[] = [startRangePeak]
      let metricsPeak: any[] = [metricsPeakXaxisValues[0], 0]
      const metricsPeakNoData: any[] = [metricsPeakXaxisValues[0], 0]
      let isPeakEmpty = true
      for (let i = 1; i <= payload.filter; i += 1) {
        if (i === payload.filter) {
          metricsPeakXaxisValues[i] = endRange
          metricsPeak[i] = [endRange, 0]
          metricsPeakNoData[i] = [endRange, 0]
        } else {
          metricsPeakXaxisValues[i] = startRangePeak + i * 24 * 60 * 60 * 1000
        }
      }

      for (let i = 0; i < payload.filter; i += 1) {
        metricsPeak[i] = [metricsPeakXaxisValues[i], 0]
        metricsPeakNoData[i] = [metricsPeakXaxisValues[i], 0]
      }
      let successPeak: any = { data: false }

      const peakCallServiceParams = { startRange: metricsPeakXaxisValues[0], endRange }
      const getMetricCalls = yield call(
        getMetricsByType as any,
        payload.data,
        getAppMetricHost(payload.appSettings),
        payload.type,
        peakCallServiceParams,
      )
      if (getMetricCalls.data !== undefined && getMetricCalls.data) {
        successPeak = getMetricCalls.data
      } else if (getMetricCalls.err_code !== undefined) {
        requestTimeOut = true
      }
      if (!requestTimeOut) {
        if (getMetricCalls.data) {
          const metricsPeakDataResponse: any = successPeak
          if (metricsPeakDataResponse.length > 0) {
            _.forEach(metricsPeakDataResponse, function fn(mdx, key) {
              const checkIndex = _.findIndex(metricsPeakXaxisValues, function fn2(mmau: any) {
                const day = getMetricDayTimestamp(mmau)
                const month = getMetricMonthTimestamp(mmau)
                const year = getMetricYearTimestamp(mmau)
                return mdx.day === day && mdx.month === month && mdx.year === year
              })
              if (checkIndex >= 0) {
                isPeakEmpty = false
                if (mdx.day > 0 && mdx.year > 0 && mdx.month >= 0) {
                  const date = new Date(mdx.year, mdx.month, mdx.day, 0, 0, 0)
                  const timestampLocal = date.getTime()

                  metricsPeak[checkIndex] = [timestampLocal, metricsPeakDataResponse[key].ccu]
                  const monthToday = getMetricMonthTimestamp(endRange)
                  if (metricsPeakDataResponse[key].ccu > metricsAUCPeak && monthToday === mdx.month)
                    metricsAUCPeak = metricsPeakDataResponse[key].ccu
                }
              }
            })
          }
        }
      }
      if (isPeakEmpty) {
        metricsPeak = []
      }

      if (!metricsPlanSupport) {
        metricsPeak = metricsPeakNoData
      }
      if (requestTimeOut) {
        window.triggerNotification?.('Request Time-out ', 'error')
      } else {
        yield put({
          type: actions.SET_STATE,
          payload: {
            metricsPeak,
            metricsAUCPeak,
            metricsUpdateSuccess: true,
          },
        })
      }
    } else if (payload.type === 'dau') {
      // DAU
      let metricsAUCDay = '0'

      const startRangeTempDAU = endRange - (payload.filter - 1) * 24 * 60 * 60 * 1000
      const startRangeTempDateDAU = new Date(startRangeTempDAU)
      const startRangeDateDAU = new Date(
        startRangeTempDateDAU.getFullYear(),
        startRangeTempDateDAU.getMonth(),
        startRangeTempDateDAU.getDate(),
      )
      const startRangeDAU = startRangeDateDAU.getTime()

      const metricsDauXaxisValues: any[] = [startRangeDAU]

      let metricsDau: any[] = [metricsDauXaxisValues[0], 0]
      const metricsDauNoData: any[] = [metricsDauXaxisValues[0], 0]
      let isDAUEmpty = true
      for (let i = 1; i <= payload.filter; i += 1) {
        if (i === payload.filter) {
          metricsDauXaxisValues[i] = endRange
          metricsDau[i] = [endRange, 0]
          metricsDauNoData[i] = [endRange, 0]
        } else {
          metricsDauXaxisValues[i] = startRangeDAU + i * 24 * 60 * 60 * 1000
        }
      }

      for (let i = 0; i < payload.filter; i += 1) {
        metricsDau[i] = [metricsDauXaxisValues[i], 0]
        metricsDauNoData[i] = [metricsDauXaxisValues[i], 0]
      }
      let dauServiceParams = {}
      dauServiceParams = {
        startRange: metricsDauXaxisValues[0],
        endRange,
      }
      let successDau: any = { data: false }

      const getMetricCalls = yield call(
        getMetricsByType as any,
        payload.data,
        getAppMetricHost(payload.appSettings),
        payload.type,
        dauServiceParams,
      )
      if (getMetricCalls.data !== undefined && getMetricCalls.data) {
        successDau = getMetricCalls.data
      } else if (getMetricCalls.err_code !== undefined) {
        requestTimeOut = true
      }
      if (!requestTimeOut) {
        if (getMetricCalls.data) {
          const metricsDauDataResponse: any = successDau
          if (metricsDauDataResponse.length > 0) {
            _.forEach(metricsDauDataResponse, function fn(mdx, key) {
              const checkIndex = _.findIndex(metricsDauXaxisValues, function fn2(mmau: any) {
                const day = getMetricDayTimestamp(mmau)
                const month = getMetricMonthTimestamp(mmau)
                const year = getMetricYearTimestamp(mmau)
                return mdx.day === day && mdx.month === month && mdx.year === year
              })
              if (checkIndex >= 0) {
                isDAUEmpty = false
                if (mdx.day > 0 && mdx.year > 0 && mdx.month >= 0) {
                  const date = new Date(mdx.year, mdx.month, mdx.day, 0, 0, 0)
                  const timestampLocal = date.getTime()
                  metricsDau[checkIndex] = [timestampLocal, metricsDauDataResponse[key].DAU]

                  const dayToday = getMetricDayTimestamp(endRange)
                  const monthToday = getMetricMonthTimestamp(endRange)
                  const yearToday = getMetricYearTimestamp(endRange)
                  if (mdx.day === dayToday && mdx.month === monthToday && mdx.year === yearToday)
                    metricsAUCDay = metricsDauDataResponse[key].DAU
                }
              }
            })
          }
        }
      }
      metricsDau = _.compact(metricsDau)
      if (isDAUEmpty) {
        metricsDau = []
      }
      if (!metricsPlanSupport) {
        metricsDau = metricsDauNoData
      }
      if (requestTimeOut) {
        window.triggerNotification?.('Request Time-out ', 'error')
      } else {
        yield put({
          type: actions.SET_STATE,
          payload: {
            metricsDau,
            metricsAUCDay,
            metricsUpdateSuccess: true,
          },
        })
      }
    } else if (payload.type === 'mau') {
      let metricsAUCMonth: string | number = '0'

      // MAU
      let metricsMauXaxisValues: any[] = [
        getTimestampOfMonth(2, 1) * 1000,
        getTimestampOfMonth(1) * 1000,
        endRange,
      ]
      // Compared against the number, not the string `'6'` the legacy code used —
      // that comparison could never be true. This path has no live dispatcher.
      if (payload.filter === 6) {
        metricsMauXaxisValues = [
          getTimestampOfMonth(5, 1) * 1000,
          getTimestampOfMonth(4) * 1000,
          getTimestampOfMonth(3) * 1000,
          getTimestampOfMonth(2) * 1000,
          getTimestampOfMonth(1) * 1000,
          endRange,
        ]
      }

      let metricsMau: any[] = []
      const metricsMauNoData: any[] = []
      let isMAUEmpty = true
      for (let i = 0; i < payload.filter - 1; i += 1) {
        metricsMau[i] = {
          x: getMetricMonthFromTimestamp(metricsMauXaxisValues[i]),
          y: 0,
        }
        metricsMauNoData[i] = {
          x: getMetricMonthFromTimestamp(metricsMauXaxisValues[i]),
          y: 0,
        }
      }
      let mauServiceParams = {}
      mauServiceParams = {
        startRange: getTimestampOfMonth(payload.filter - 1, 1) * 1000,
        endRange,
      }
      let successMau: any = { data: false }
      const getMetricCalls = yield call(
        getMetricsByType as any,
        payload.data,
        getAppMetricHost(payload.appSettings),
        payload.type,
        mauServiceParams,
      )
      if (getMetricCalls.data !== undefined && getMetricCalls.data) {
        successMau = getMetricCalls.data
      } else if (getMetricCalls.err_code !== undefined) {
        requestTimeOut = true
      }
      if (!requestTimeOut) {
        if (getMetricCalls.data) {
          const metricsMauDataResponse: any = successMau
          if (metricsMauDataResponse.length > 0) {
            _.forEach(metricsMauDataResponse, function fn(mdx, key) {
              const checkIndex = _.findIndex(metricsMauXaxisValues, function fn2(mmau: any) {
                const month = getMetricMonthTimestamp(mmau)
                const year = getMetricYearTimestamp(mmau)

                return mdx.month === month && mdx.year === year
              })
              if (checkIndex >= 0) {
                isMAUEmpty = false
                if (mdx.year > 0 && mdx.month >= 0) {
                  const date = new Date(mdx.year, mdx.month, 2, 0, 0, 0)
                  const timestampLocal = date.getTime() - timezoneDiffInMs
                  metricsMau[checkIndex] = {
                    x: getMetricMonthFromTimestamp(timestampLocal),
                    y: metricsMauDataResponse[key].MAU,
                  }
                  const monthToday = getMetricMonthTimestamp(endRange)
                  const yearToday = getMetricYearTimestamp(endRange)
                  if (mdx.month === monthToday && mdx.year === yearToday)
                    metricsAUCMonth = metricsMauDataResponse[key].MAU
                }
              }
            })
          }
        }
      }
      metricsMau = _.compact(metricsMau)
      if (isMAUEmpty) {
        metricsMau = []
      }
      if (!metricsPlanSupport) {
        metricsMau = metricsMauNoData
      }
      if (requestTimeOut) {
        window.triggerNotification?.('Request Time-out ', 'error')
      } else {
        yield put({
          type: actions.SET_STATE,
          payload: {
            metricsMau,
            metricsAUCMonth,
            metricsUpdateSuccess: true,
          },
        })
      }
    } else if (payload.type === 'voice') {
      let metricsAVoMMonth = 0
      let metricsAVoMToday = '0'
      const startRangeVoiceTemp = endRange - (payload.filter - 1) * 24 * 60 * 60 * 1000
      const startRangeVoiceTempDate = new Date(startRangeVoiceTemp)
      const startRangeVoiceDate = new Date(
        startRangeVoiceTempDate.getFullYear(),
        startRangeVoiceTempDate.getMonth(),
        startRangeVoiceTempDate.getDate(),
      )
      const startRangeVoice = startRangeVoiceDate.getTime()
      const metricsVoiceXaxisValues: any[] = [startRangeVoice]
      let metricsVoice: any[] = [metricsVoiceXaxisValues[0], 0]

      const metricsVoiceNoData: any[] = [metricsVoiceXaxisValues[0], 0]

      for (let i = 0; i < payload.filter; i += 1) {
        metricsVoiceXaxisValues[i] = startRangeVoice + i * 24 * 60 * 60 * 1000
        metricsVoice[i] = [metricsVoiceXaxisValues[i], 0]
        metricsVoiceNoData[i] = [metricsVoiceXaxisValues[i], 0]
      }
      let successVoice: any = { data: false }

      const voiceVideoCallServiceParams = {
        startRange: metricsVoiceXaxisValues[0],
        endRange,
      }
      const getMetricCalls = yield call(
        getMetricsByType as any,
        payload.data,
        payload.type,
        voiceVideoCallServiceParams,
      )
      if (getMetricCalls.data !== undefined && getMetricCalls.data) {
        successVoice = getMetricCalls.data
      } else if (getMetricCalls.err_code !== undefined) {
        requestTimeOut = true
      }

      if (!requestTimeOut) {
        if (getMetricCalls.data) {
          const metricsVoiceVideoDataResponse: any = successVoice
          if (metricsVoiceVideoDataResponse.length > 0) {
            _.forEach(metricsVoiceXaxisValues, function fn(mvxv, key) {
              const checkIndex = _.findIndex(metricsVoiceVideoDataResponse, function fn2(
                mvvdr: any,
              ) {
                const tempMonth = mvvdr.month - 1
                const date = new Date(mvvdr.year, tempMonth, mvvdr.day, 0, 0, 0)
                const timestampLocal = date.getTime()
                return mvxv === timestampLocal
              })
              if (checkIndex >= 0) {
                metricsVoice[key] = [mvxv, metricsVoiceVideoDataResponse[checkIndex].voice_minutes]

                const monthToday = getMetricMonthTimestamp(endRange) + 1
                const dayToday = getMetricDayTimestamp(endRange)

                if (monthToday === metricsVoiceVideoDataResponse[checkIndex].month) {
                  metricsAVoMMonth += metricsVoiceVideoDataResponse[checkIndex].voice_minutes
                }
                if (dayToday === metricsVoiceVideoDataResponse[checkIndex].day) {
                  metricsAVoMToday = metricsVoiceVideoDataResponse[checkIndex].voice_minutes
                }
              }
            })
          }
        } else {
          metricsVoice = []
        }
      }
      metricsVoice = _.compact(metricsVoice)

      if (requestTimeOut) {
        window.triggerNotification?.('Request Time-out ', 'error')
      } else {
        yield put({
          type: actions.SET_STATE,
          payload: {
            metricsVoice,
            metricsAVoMMonth,
            metricsAVoMToday,
            metricsUpdateSuccess: true,
          },
        })
      }
    } else if (payload.type === 'video') {
      let metricsAViMMonth = 0
      let metricsAViMToday = '0'
      const startRangeVideoTemp = endRange - (payload.filter - 1) * 24 * 60 * 60 * 1000
      const startRangeVideoTempDate = new Date(startRangeVideoTemp)
      const startRangeVideoDate = new Date(
        startRangeVideoTempDate.getFullYear(),
        startRangeVideoTempDate.getMonth(),
        startRangeVideoTempDate.getDate(),
      )
      const startRangeVideo = startRangeVideoDate.getTime()
      const metricsVideoXaxisValues: any[] = [startRangeVideo]
      let metricsVideo: any[] = [metricsVideoXaxisValues[0], 0]
      const metricsVideoNoData: any[] = [metricsVideoXaxisValues[0], 0]

      for (let i = 0; i < payload.filter; i += 1) {
        metricsVideoXaxisValues[i] = startRangeVideo + i * 24 * 60 * 60 * 1000
        metricsVideo[i] = [metricsVideoXaxisValues[i], 0]
        metricsVideoNoData[i] = [metricsVideoXaxisValues[i], 0]
      }

      let successVideo: any = { data: false }
      const voiceVideoCallServiceParams = {
        startRange: metricsVideoXaxisValues[0],
        endRange,
      }
      const getMetricCalls = yield call(
        getMetricsByType as any,
        payload.data,
        payload.type,
        voiceVideoCallServiceParams,
      )
      if (getMetricCalls.data !== undefined && getMetricCalls.data) {
        successVideo = getMetricCalls.data
      } else if (getMetricCalls.err_code !== undefined) {
        requestTimeOut = true
      }

      if (!requestTimeOut) {
        if (getMetricCalls.data) {
          const metricsVoiceVideoDataResponse: any = successVideo
          if (metricsVoiceVideoDataResponse.length > 0) {
            _.forEach(metricsVideoXaxisValues, function fn(mvxv, key) {
              const checkIndex = _.findIndex(metricsVoiceVideoDataResponse, function fn2(
                mvvdr: any,
              ) {
                const tempMonth = mvvdr.month - 1
                const date = new Date(mvvdr.year, tempMonth, mvvdr.day, 0, 0, 0)
                const timestampLocal = date.getTime()
                return mvxv === timestampLocal
              })
              if (checkIndex >= 0) {
                metricsVideo[key] = [mvxv, metricsVoiceVideoDataResponse[checkIndex].video_minutes]
                const monthToday = getMetricMonthTimestamp(endRange) + 1
                const dayToday = getMetricDayTimestamp(endRange)
                if (monthToday === metricsVoiceVideoDataResponse[checkIndex].month) {
                  metricsAViMMonth += metricsVoiceVideoDataResponse[checkIndex].video_minutes
                }
                if (dayToday === metricsVoiceVideoDataResponse[checkIndex].day) {
                  metricsAViMToday = metricsVoiceVideoDataResponse[checkIndex].video_minutes
                }
              }
            })
          }
        } else {
          metricsVideo = []
        }
      }
      metricsVideo = _.compact(metricsVideo)

      if (requestTimeOut) {
        window.triggerNotification?.('Request Time-out ', 'error')
      } else {
        yield put({
          type: actions.SET_STATE,
          payload: {
            metricsVideo,
            metricsAViMMonth,
            metricsAViMToday,
            metricsUpdateSuccess: true,
          },
        })
      }
    }
    return true
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        metricsLoading: false,
      },
    })
    window.triggerNotification?.('Failed to fetch metrics', 'error')
  }
}

export default function* metricsSaga() {
  yield all([
    takeEvery(actions.GET_METRICS, GET_METRICS as SagaWorker),
    takeEvery(actions.GET_METRICS_BY_FILTER, GET_METRICS_BY_FILTER as SagaWorker),
  ])
}
